import { isProxy, toRaw } from "vue";

const MAX_STR = 2000; // avoid huge payloads

function truncate(s, n = MAX_STR) {
  if (typeof s !== "string") s = String(s ?? "");
  return s.length > n ? s.slice(0, n) + "…<truncated>" : s;
}

function pickPrimitives(obj, allowList = []) {
  // Return only primitive props + any explicit allow-listed keys
  if (!obj || typeof obj !== "object") return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    const val = isProxy(v) ? toRaw(v) : v;
    if (
      val == null ||
      typeof val === "string" ||
      typeof val === "number" ||
      typeof val === "boolean"
    ) {
      out[k] = typeof val === "string" ? truncate(val) : val;
    } else if (allowList.includes(k)) {
      // allow-list a few known sub-objects (still prune to primitives)
      out[k] = pickPrimitives(val);
    }
    // everything else (functions, objects, arrays) is skipped
  }
  return out;
}

function serializeAxios(err) {
  // Minimal axios context if present
  const cfg = err?.config || {};
  const res = err?.response || {};
  const req = err?.request || {};
  return {
    method: cfg.method,
    baseURL: cfg.baseURL,
    url: cfg.url,
    status: res.status,
    statusText: res.statusText,
    timeout: cfg.timeout,
    withCredentials: cfg.withCredentials,
    // do NOT include headers/body to avoid PII and size
  };
}

function serializeError(err) {
  if (!err) return null;
  const base = {
    name: err.name,
    message: truncate(err.message),
    stack: truncate(err.stack || "", 1600), // keep a few frames
  };
  if (err.cause) {
    base.cause =
      typeof err.cause === "string"
        ? truncate(err.cause)
        : truncate(err.cause?.message || String(err.cause));
  }
  if (err.isAxiosError || err?.config) {
    base.http = serializeAxios(err);
  } else if (err?.response && err?.response?.status) {
    // fetch-style error info if you wrap it
    base.http = { status: err.response.status, statusText: err.response.statusText };
  }
  if (err.code) base.code = err.code;
  return base;
}

function serializeInstance(instance) {
  if (!instance) return null;
  // Unwrap proxies to avoid circulars
  const inst = isProxy(instance) ? toRaw(instance) : instance;
  const type = inst.type || {};
  const proxy = inst.proxy || {};

  const route = proxy.$route
    ? {
        name: proxy.$route.name,
        path: proxy.$route.path,
        // Keep params/query shallow and primitive-only
        params: pickPrimitives(proxy.$route.params),
        query: pickPrimitives(proxy.$route.query),
      }
    : undefined;

  return {
    name: type.name || type.__name,
    file: type.__file,
    uid: inst.uid,
    // only primitives from props (trim big strings)
    props: pickPrimitives(inst.props || {}),
    route,
  };
}

export function buildErrorMeta(err, instance, info) {
  return {
    info, // this is a small string Vue provides
    error: serializeError(err),
    component: serializeInstance(instance),
    time: new Date().toISOString(),
  };
}
